import axios from 'axios';

const localStorageKey = 'pdfToPrinter';
// The port the county-side print helper actually listens on. This is the app's
// frozen contract — it has been 3010 in every version of it, and it walks up to
// 3019 only when 3010 is already taken, never down. The previous value here
// (3003) matched nothing, so a workstation that left Printer Port blank built a
// URL to a port nothing served; window.open() does not throw on a refused
// connection, so printerHandler still reported "sent to printer". Exported so
// the Printer Setting screen pre-fills the same number rather than making the
// county guess it.
export const DEFAULT_PRINTER_PORT = 3010;

export const getPrinterConfig = () => {
  const localStorageData = localStorage.getItem(localStorageKey) || {};
  let pdfToPrinter;
  try {
    pdfToPrinter = localStorageData ? JSON.parse(localStorageData) : {};
  } catch (e) {
    pdfToPrinter = {};
  }
  return pdfToPrinter;
};

export const setPrinterConfig = (printerConfig) => {
  localStorage.setItem(localStorageKey, JSON.stringify(printerConfig));
};

export const setPrinterMode = (mode) => {
  const printerConfig = getPrinterConfig();
  printerConfig.printerMode = mode;
  localStorage.setItem(localStorageKey, JSON.stringify(printerConfig));
};

// How each document is named to the print toolkit, and the label and group it
// shows under on the toolkit's Printer tab. Where a document prints - or that it
// opens as a preview - is chosen there, per PC, not here. A type missing from
// this map still works; the toolkit just lists it by its name.
//
// The first seven are the types every toolkit has always known; an older
// toolkit only knows these, so only these may be sent to its /pdf-to-printer.
export const REPORT_TYPES = {
  taxStatements: { label: 'Tax Statements', group: 'Tax', legacy: true },
  infoSheets: { label: 'Info Sheets', group: 'Tax', legacy: true },
  paidReceipts: { label: 'Paid Receipts', group: 'Tax', legacy: true },
  mortgageTax: { label: 'Mortgage Tax', group: 'Bookkeeping', legacy: true },
  miscReceipts: { label: 'Misc Receipts', group: 'Bookkeeping', legacy: true },
  checks: { label: 'Checks', group: 'Bookkeeping', legacy: true },
  depositSlips: { label: 'Deposit Slips', group: 'Bookkeeping', legacy: true },
  infoLists: { label: 'Info Lists (Pick List)', group: 'Tax' },
  addressLabels: { label: 'Address Labels', group: 'Tax' },
  depositLists: { label: 'Deposit Lists', group: 'Bookkeeping' },
  miscEonApp: { label: 'Misc EON/App', group: 'Bookkeeping' },
};

const isLegacyType = (reportName) => Boolean(REPORT_TYPES[reportName] && REPORT_TYPES[reportName].legacy);

// A workstation that switched a document off on the old per-document switches
// keeps that choice: those switches are gone, but an explicit false still saved
// in localStorage means "preview" until the toolkit's Printer tab takes over.
export const isAutoPrintAllowed = (pdfToPrinter, reportName) => {
  if (!reportName) return true;
  const { autoPrint } = pdfToPrinter || {};
  if (!autoPrint) return true;
  return autoPrint[reportName] !== false;
};

const toolkitBase = (pdfToPrinter) => `http://localhost:${pdfToPrinter.printerPort || DEFAULT_PRINTER_PORT}`;

// Whether this PC's print toolkit has ever answered back (protocol 2). Kept in
// localStorage: a PC that has the new toolkit keeps it. Until it has, the ERP
// behaves exactly as it always has - nearly every PC has only the older helper.
const PROTOCOL_KEY = 'printToolkitProtocol';
export const knowsToolkitAnswers = () => {
  try { return localStorage.getItem(PROTOCOL_KEY) === '2'; } catch (e) { return false; }
};
const rememberToolkitAnswers = () => {
  try { localStorage.setItem(PROTOCOL_KEY, '2'); } catch (e) { /* storage blocked */ }
};

// How long an older helper's tab stays open before we close it (unchanged), and
// how long to wait for a print to finish once the toolkit has accepted it. An
// object so specs can shorten the waits.
export const printerTiming = { answerMs: 5000, resultMs: 90 * 1000 };

// Ties the toolkit's answers to the request that asked, so two prints in a row
// cannot pick up each other's answer.
const newNonce = () => {
  const bytes = new Uint8Array(12);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

// Resolves with the toolkit's final answer for one request, or null when nothing
// answered within answerMs.
const listenToToolkit = (tab, origin, nonce, isFinal) => new Promise((resolve) => {
  let answered = false;
  let finished = false;
  let timer = null;
  let listener = null;
  const done = (value) => {
    if (finished) return;
    finished = true;
    clearTimeout(timer);
    window.removeEventListener('message', listener);
    resolve(value);
  };
  listener = (event) => {
    if (event.origin !== origin) return;
    if (event.source && event.source !== tab) return;
    const data = event.data || {};
    if (data.source !== 'regov-county-toolkit' || data.nonce !== nonce) return;
    if (!answered) {
      answered = true;
      clearTimeout(timer);
      timer = setTimeout(() => done({ action: 'timeout' }), printerTiming.resultMs);
    }
    if (isFinal(data)) done(data);
  };
  window.addEventListener('message', listener);
  timer = setTimeout(() => done(null), printerTiming.answerMs);
});

const blockedPopup = (thisObject) => {
  thisObject.$store.dispatch('flashError', 'Could not open the print window - your browser blocked it. Allow pop-ups for this site, then print again.');
  return { action: 'blocked' };
};

// Sends a report to this PC's print helper. Call sites that cannot print direct
// (deafultPrinterMode false) and PCs with printing off just open the report in a
// tab, as before.
//
// The request is the same GET /pdf-to-printer the ERP has always opened, plus
// v=2. An older helper ignores v=2 and prints exactly as it does today; we close
// its tab after a few seconds, as we always have. The newer toolkit instead
// decides - print, or preview - and answers back from that tab: "printed on X"
// (it closes the tab itself) or "preview" (the tab becomes the PDF).
//
// A document an older helper does not know (anything beyond the original seven)
// is only sent to a PC whose toolkit has answered before. Anywhere else it opens
// as a plain preview - an older helper would print it on the Windows default
// printer.
export const printerHandler = async (thisObject, { url, reportName = '', deafultPrinterMode = false }) => {
  const pdfToPrinter = getPrinterConfig();
  if (!(pdfToPrinter.isPrinterEnabled
    && deafultPrinterMode
    && isAutoPrintAllowed(pdfToPrinter, reportName))) {
    window.open(url, '_blank');
    return { action: 'tab' };
  }

  const knownToolkit = knowsToolkitAnswers();
  if (!isLegacyType(reportName) && !knownToolkit) {
    window.open(url, '_blank');
    return { action: 'preview', reason: 'no-toolkit-answer-yet' };
  }

  const base = toolkitBase(pdfToPrinter);
  const meta = REPORT_TYPES[reportName] || {};
  const nonce = newNonce();
  const query = [
    ['url', url], ['reportName', reportName], ['v', '2'], ['nonce', nonce],
    ['label', meta.label || ''], ['group', meta.group || ''],
  ].map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  const tab = window.open(`${base}/pdf-to-printer?${query}`, '_blank');
  if (!tab) return blockedPopup(thisObject);

  // Today's behaviour on a PC that has never had a toolkit answer: say it was
  // sent straight away. If a toolkit answers after all, its answer takes over.
  if (!knownToolkit) thisObject.$store.dispatch('flashSuccess', 'Receipt/report sent to printer');

  const result = await listenToToolkit(tab, base, nonce,
    (data) => ['printed', 'preview', 'refused'].includes(data.action));

  if (result === null) {
    if (!knownToolkit) {
      // An older helper: it has printed already. Close its tab, as before.
      try { tab.close(); } catch (e) { /* already closed */ }
      return { action: 'legacy' };
    }
    thisObject.$store.dispatch('flashError', 'The print toolkit did not answer. If nothing printed, check that RegovCountyToolkit is running.');
    return { action: 'no-answer' };
  }

  rememberToolkitAnswers();
  if (result.action === 'printed') {
    thisObject.$store.dispatch('flashSuccess', `Sent to ${result.printer}`);
  } else if (result.action === 'preview' && result.reason === 'print-failed') {
    thisObject.$store.dispatch('flashError', `Could not print${result.error ? ` (${result.error})` : ''} - showing it on screen instead.`);
  } else if (result.action === 'refused') {
    thisObject.$store.dispatch('flashError', 'The print toolkit refused this request - showing it on screen instead.');
    // eslint-disable-next-line no-param-reassign
    tab.location.href = url;
  } else if (result.action === 'timeout') {
    thisObject.$store.dispatch('flashError', 'The print toolkit did not finish - check the printer.');
  }
  return result;
};

// For the Printer Setting screen's Test button: opens the toolkit's hello page,
// which answers with its version and closes. Resolves { version }, null, or
// { blocked: true }.
export const testToolkitConnection = async (pdfToPrinter = getPrinterConfig()) => {
  const base = toolkitBase(pdfToPrinter);
  const nonce = newNonce();
  const tab = window.open(`${base}/print/hello?nonce=${nonce}`, '_blank');
  if (!tab) return { blocked: true };
  const result = await listenToToolkit(tab, base, nonce, (data) => data.action === 'hello');
  if (!result) {
    try { tab.close(); } catch (e) { /* already closed */ }
    return null;
  }
  rememberToolkitAnswers();
  return { version: result.version };
};

export const getPrinterList = async () => {
  const pdfToPrinter = getPrinterConfig();
  const printerPort = pdfToPrinter.printerPort || DEFAULT_PRINTER_PORT;
  try {
    const response = await axios.get(`http://localhost:${printerPort}/get-printers`);
    return response.data;
  } catch (error) {
    console.error('Error getting printer list:', error);
    return [];
  }
};

export default {};
